/*
 * <matuse-size-helper> — height + weight in, Matuse size out.
 * One file, no dependencies. Used by the wetsuit finder and droppable into any product page:
 *   {% render 'size-helper', product: product %}   (snippets/size-helper.liquid)
 * or by hand:
 *   <matuse-size-helper gender="MEN" size-guide="/pages/size-fit-guide" contact="/pages/contact"></matuse-size-helper>
 *
 * Attributes: gender (MEN | WOMEN | YOUTH), units (imperial | metric), height (inches) + weight (lb) to start
 * filled in, size-guide, contact, select-variant (on a product page, click the matching Size option),
 * no-remember (don't keep the entry in localStorage). Optional child <script type="application/json"> with
 * { "copy": {...}, "sizes": [{ "n": "ML", "label": "ML", "available": true, "id": 123 }] }.
 * Events on the element: size-helper:result (detail = result below) and size-helper:clear.
 * window.MatuseSizeHelper = { CHARTS, sizeFor, normSize } for other scripts.
 */
(function () {
  'use strict';
  if (window.customElements && customElements.get('matuse-size-helper')) return;

  /* Charts transcribed from /pages/size-chart-men, -women, -youth (2026-10-19). Height in inches, weight in lb.
     The ft/lb columns are the source; the women's cm/kg columns disagree for size 2. Youth is height only. */
  const CHARTS = {
    MEN: [
      ['XS', 63, 66, 120, 135], ['S', 65, 67, 135, 145], ['ST', 68, 70, 135, 150], ['MS', 66, 68, 145, 155],
      ['M', 68, 70, 150, 165], ['MT', 70, 72, 165, 175], ['ML', 69, 71, 160, 175], ['LS', 67, 69, 160, 180],
      ['L', 70, 73, 170, 190], ['LT', 74, 76, 180, 200], ['XLS', 68, 70, 180, 200], ['XL', 71, 74, 190, 215],
      ['XXLS', 69, 71, 205, 230], ['XLT', 74, 77, 205, 220], ['XXL', 72, 75, 205, 230], ['XXXL', 73, 78, 230, 260]
    ],
    WOMEN: [
      ['2', 61, 63, 90, 105], ['4', 63, 65, 100, 115], ['6S', 62, 64, 110, 120], ['6', 64, 66, 110, 125],
      ['6T', 66, 69, 115, 135], ['8S', 63, 65, 120, 135], ['8', 65, 67, 125, 140], ['8T', 67, 70, 125, 140],
      ['10S', 63, 66, 130, 145], ['10', 66, 68, 135, 150], ['10T', 69, 72, 140, 155], ['12', 67, 69, 140, 160],
      ['14', 69, 71, 150, 170]
    ],
    YOUTH: [
      ['6Y', 48.8, 52.0, null, null], ['8Y', 52.0, 55.1, null, null], ['10Y', 55.1, 57.9, null, null],
      ['12Y', 57.9, 60.6, null, null], ['14Y', 60.6, 63.4, null, null], ['16Y', 63.4, 66.1, null, null]
    ]
  };
  const COPY = {
    title: 'Check your size.',
    lede: '',
    height: 'Height', weight: 'Weight', weightOptional: 'Weight (optional for youth)',
    imperial: 'ft / lb', metric: 'cm / kg',
    readout: 'Your size', between: 'Between sizes, the next closest is', sizeUp: 'On the high end of a range? Size up.',
    outside: 'You sit outside our size chart. Write to us and we will size you by hand.',
    submit: 'Check my size', change: 'Change', clear: 'Clear', cancel: 'Cancel',
    inStock: 'in stock', soldOut: 'sold out', altInStock: 'in stock, the next closest fit', notMade: 'Not made in',
    sizeGuide: 'Size guide', contact: 'Still deciding? Write to us.'
  };
  const STORE_KEY = 'matuse:size-helper';

  function normSize(v) {
    return String(v || '').toUpperCase().replace(/\s+/g, '').replace(/^2XL/, 'XXL').replace(/^3XL/, 'XXXL');
  }
  // Rank every size by how far the person sits outside its ranges (inches, and pounds / 6 so a 6 lb miss
  // weighs the same as a 1 in miss), plus a small pull toward the middle of the range.
  function sizeFor(who, inches, lbs) {
    const chart = CHARTS[who] || CHARTS.MEN;
    if (!inches) return null;
    const ranked = chart.map(function (r) {
      const out = function (x, lo, hi) { return x < lo ? lo - x : (x > hi ? x - hi : 0); };
      const dh = out(inches, r[1], r[2]);
      const dw = (r[3] != null && lbs) ? out(lbs, r[3], r[4]) / 6 : 0;
      const ch = (inches - (r[1] + r[2]) / 2) / Math.max(1, r[2] - r[1]);
      const cw = (r[3] != null && lbs) ? (lbs - (r[3] + r[4]) / 2) / Math.max(1, r[4] - r[3]) : 0;
      const highEnd = (r[3] != null && lbs) ? (lbs > r[3] + (r[4] - r[3]) * 0.8) : false;
      return { size: r[0], score: dh * dh + dw * dw + 0.15 * (ch * ch + cw * cw), highEnd: highEnd && dh === 0 && dw === 0 };
    }).sort(function (x, y) { return x.score - y.score; });
    return { primary: ranked[0].size, alt: ranked[1] ? ranked[1].size : null, close: !!ranked[1] && ranked[1].score - ranked[0].score < 0.12, highEnd: ranked[0].highEnd, fits: ranked[0].score < 1.2, inches: inches, lbs: lbs || 0 };
  }

  const CSS = `
matuse-size-helper { display: block; --msh-text: rgb(var(--text-color, 28 28 28)); --msh-bg: rgb(var(--background, 255 255 255)); --msh-bg2: rgb(var(--background-secondary, 250 244 240)); --msh-ink: rgb(var(--button-background-primary, 50 30 30)); --msh-ink-text: rgb(var(--button-text-primary, 255 255 255)); --msh-line: rgb(var(--text-color, 28 28 28) / 0.16); --msh-line-strong: rgb(var(--text-color, 28 28 28) / 0.9); --msh-muted: rgb(var(--text-color, 28 28 28) / 0.62); --msh-accent: rgb(var(--wf-accent-rgb, 147 213 218)); --msh-heading: var(--heading-font-family, "Bricolage Grotesque", "Helvetica Neue", Arial, sans-serif); --msh-body: var(--text-font-family, "Instrument Sans", "Helvetica Neue", Arial, sans-serif); --msh-caps: var(--heading-text-transform, uppercase); color: var(--msh-text); font-family: var(--msh-body); font-size: 1rem; line-height: 1.5; }
.msh { background: transparent; border: 1px solid var(--msh-line); padding: 1.2rem 1.3rem; display: grid; gap: 0.9rem; }
.msh *, .msh *::before { box-sizing: border-box; }
.msh button { font: inherit; color: inherit; background: none; border: 0; padding: 0; cursor: pointer; }
.msh__title { font-family: var(--msh-heading); font-weight: 500; text-transform: var(--msh-caps); font-size: clamp(1.15rem, 0.95rem + 0.8vw, 1.5rem); line-height: 1.05; margin: 0; }
.msh__lede { margin: -0.3rem 0 0; color: var(--msh-muted); font-size: 0.95rem; }
.msh__form { display: grid; gap: 1.1rem; }
.msh__fields { display: grid; gap: 0.75rem; grid-template-columns: 1fr; align-items: end; }
.msh__field { display: grid; gap: 0.35rem; }
.msh__label { font-size: 0.72rem; letter-spacing: 0.12em; text-transform: uppercase; color: var(--msh-muted); }
.msh__inputs { display: flex; gap: 0.5rem; }
.msh__wrap { position: relative; flex: 1 1 0; min-width: 0; }
.msh .msh__input { width: 100%; min-width: 0; padding: 0.75rem 2.6rem 0.75rem 0.9rem; font: inherit; font-size: 1.05rem; color: var(--msh-text); background: var(--msh-bg); border: 1px solid var(--msh-line-strong); border-radius: 0; -moz-appearance: textfield; }
.msh .msh__input::-webkit-outer-spin-button, .msh .msh__input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.msh .msh__input:focus { outline: 2px solid var(--msh-ink); outline-offset: 1px; }
.msh__unit { position: absolute; right: 0.9rem; top: 50%; transform: translateY(-50%); font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase; color: var(--msh-muted); pointer-events: none; }
.msh__units { display: inline-flex; border: 1px solid var(--msh-line-strong); justify-self: start; }
.msh .msh__unitbtn { padding: 0.55rem 0.9rem; font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase; }
.msh .msh__unitbtn + .msh__unitbtn { border-left: 1px solid var(--msh-line-strong); }
.msh .msh__unitbtn.is-on { background: var(--msh-ink); color: var(--msh-ink-text); }
.msh__readout { display: flex; flex-wrap: wrap; align-items: baseline; gap: 0.3rem 0.8rem; margin: 0; padding: 0.75rem 0.9rem; background: var(--msh-bg); border: 1px solid var(--msh-line); }
.msh__readout:empty { display: none; }
.msh__ro-label { font-size: 0.72rem; letter-spacing: 0.12em; text-transform: uppercase; color: var(--msh-muted); }
.msh__ro-size { font-family: var(--msh-heading); font-weight: 500; font-size: 1.7rem; line-height: 1; text-transform: var(--msh-caps); }
.msh__ro-note { flex-basis: 100%; font-size: 0.88rem; color: var(--msh-muted); }
.msh__ro-stock { flex-basis: 100%; font-size: 0.88rem; }
.msh__ro-stock strong { font-weight: 600; }
.msh__ro-stock--ok::before { content: "●"; color: var(--msh-accent); font-size: 0.6em; vertical-align: 0.25em; margin-right: 0.5em; filter: saturate(1.4) brightness(0.85); }
.msh__actions { display: flex; flex-wrap: wrap; align-items: center; gap: 0.75rem 1.25rem; }
.msh .msh__btn { display: inline-flex; align-items: center; justify-content: center; min-height: 2.75rem; padding: 0.8rem 1.4rem; background: var(--msh-ink); color: var(--msh-ink-text); border: 1px solid var(--msh-ink); font-size: 0.8rem; letter-spacing: 0.1em; text-transform: uppercase; text-decoration: none; border-radius: 0; }
.msh .msh__btn[disabled] { opacity: 0.35; cursor: not-allowed; }
.msh .msh__link { font-size: 0.8rem; letter-spacing: 0.08em; text-transform: uppercase; text-decoration: underline; text-underline-offset: 0.3em; color: inherit; }
.msh__set { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 0.5rem 1.25rem; }
.msh__set .msh__readout { flex: 1 1 18rem; background: transparent; border: 0; padding: 0; }
.msh__set-actions { font-size: 0.88rem; color: var(--msh-muted); white-space: nowrap; }
.msh__links { display: flex; flex-wrap: wrap; gap: 0.5rem 1.25rem; }
@media (min-width: 600px) { .msh__fields { grid-template-columns: 1.3fr 1fr auto; } }
matuse-size-helper[compact] { font-size: 0.92rem; }
matuse-size-helper[compact] .msh { padding: 0.85rem 0.95rem; gap: 0.6rem; }
matuse-size-helper[compact] .msh__form { gap: 0.7rem; }
matuse-size-helper[compact] .msh__title { font-size: 1rem; }
matuse-size-helper[compact] .msh__lede { font-size: 0.85rem; }
matuse-size-helper[compact] .msh__fields { gap: 0.5rem; }
matuse-size-helper[compact] .msh__label { font-size: 0.64rem; }
matuse-size-helper[compact] .msh .msh__input { padding: 0.5rem 2.2rem 0.5rem 0.7rem; font-size: 0.95rem; }
matuse-size-helper[compact] .msh__unit { right: 0.7rem; font-size: 0.64rem; }
matuse-size-helper[compact] .msh .msh__unitbtn { padding: 0.45rem 0.7rem; font-size: 0.64rem; }
matuse-size-helper[compact] .msh .msh__btn { min-height: 2.25rem; padding: 0.55rem 1rem; font-size: 0.7rem; }
matuse-size-helper[compact] .msh .msh__link { font-size: 0.7rem; }
matuse-size-helper[compact] .msh__readout { padding: 0.55rem 0.7rem; }
matuse-size-helper[compact] .msh__ro-size { font-size: 1.35rem; }
matuse-size-helper[compact] .msh__ro-note, matuse-size-helper[compact] .msh__ro-stock { font-size: 0.82rem; }
@media (min-width: 480px) { matuse-size-helper[compact] .msh__fields { grid-template-columns: 1.3fr 1fr auto; } }
`;
  function ensureCss() {
    if (document.getElementById('matuse-size-helper-css')) return;
    const st = document.createElement('style'); st.id = 'matuse-size-helper-css'; st.textContent = CSS; document.head.appendChild(st);
  }
  function h(tag, attrs, children) {
    const el = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      if (k === 'class') el.className = attrs[k];
      else if (k.slice(0, 2) === 'on') el.addEventListener(k.slice(2), attrs[k]);
      else if (attrs[k] != null) el.setAttribute(k, attrs[k]);
    });
    (children || []).forEach(function (c) { if (c == null) return; el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
    return el;
  }
  function readStore() { try { return JSON.parse(localStorage.getItem(STORE_KEY) || 'null'); } catch (e) { return null; } }
  function writeStore(v) { try { if (v) localStorage.setItem(STORE_KEY, JSON.stringify(v)); else localStorage.removeItem(STORE_KEY); } catch (e) { /* private mode */ } }

  class SizeHelper extends HTMLElement {
    connectedCallback() {
      if (this._mounted) return; this._mounted = true;
      ensureCss();
      this.copy = Object.assign({}, COPY);
      this.sizes = null;
      const json = this.querySelector('script[type="application/json"]');
      if (json) { try { const d = JSON.parse(json.textContent); if (d.copy) Object.keys(d.copy).forEach(k => { if (typeof d.copy[k] === 'string') this.copy[k] = d.copy[k].trim(); }); if (d.sizes) this.sizes = d.sizes; } catch (e) { /* ignore */ } }
      this.gender = (this.getAttribute('gender') || 'MEN').toUpperCase();
      this.units = this.getAttribute('units') || 'imperial';
      const remembered = this.hasAttribute('no-remember') ? null : readStore();
      const hAttr = parseFloat(this.getAttribute('height')), wAttr = parseFloat(this.getAttribute('weight'));
      if (hAttr) this.entry = { h: hAttr, w: wAttr || 0 };
      else if (remembered && remembered.h) { this.entry = { h: remembered.h, w: remembered.w || 0 }; if (remembered.units) this.units = remembered.units; }
      else this.entry = null;
      this.open = !this.entry;
      this.render();
      if (this.entry) this.emit();
    }
    get result() { return this.entry ? sizeFor(this.gender, this.entry.h, this.entry.w) : null; }
    set(entry) { this.entry = entry; this.open = false; if (!this.hasAttribute('no-remember')) writeStore(entry ? { h: entry.h, w: entry.w, units: this.units } : null); this.render(); this.emit(); }
    clear() { this.entry = null; this.open = true; if (!this.hasAttribute('no-remember')) writeStore(null); this.render(); this.dispatchEvent(new CustomEvent('size-helper:clear', { bubbles: true })); }
    emit() {
      const r = this.result; if (!r) return;
      r.variant = this.stockFor(r);
      if (this.hasAttribute('select-variant')) this.selectVariant(r);
      this.dispatchEvent(new CustomEvent('size-helper:result', { bubbles: true, detail: r }));
    }
    // From the sizes list (a product page passes it): which option to point at, and whether it's in stock.
    stockFor(r) {
      if (!this.sizes || !r) return null;
      const find = function (sz, list) { const want = normSize(sz); return list.filter(function (v) { return normSize(v.n || v.label) === want || normSize(v.n || v.label) === want.replace(/Y$/, ''); })[0] || null; };
      const v1 = find(r.primary, this.sizes), v2 = r.alt ? find(r.alt, this.sizes) : null;
      if (v1 && v1.available) return { label: v1.label, id: v1.id, state: 'ok' };
      if (r.close && v2 && v2.available) return { label: v2.label, id: v2.id, state: 'alt', missing: v1 ? v1.label : r.primary };
      if (v1) return { label: v1.label, id: v1.id, state: 'out' };
      return { label: r.primary, id: null, state: 'none' };
    }
    // On a product page: click the Size option that matches, so price, stock and add-to-cart follow.
    selectVariant(r) {
      const v = r.variant; if (!v || (v.state !== 'ok' && v.state !== 'alt')) return;
      const picker = document.querySelector('variant-picker'); if (!picker) return;
      const want = normSize(v.label);
      const inputs = Array.prototype.slice.call(picker.querySelectorAll('input[type="radio"]'));
      const hit = inputs.filter(function (i) { const lab = picker.querySelector('label[for="' + i.id + '"]'); return lab && normSize(lab.textContent) === want; })[0];
      if (hit && !hit.checked) hit.click();
    }
    notes(r) {
      const C = this.copy, n = [];
      if (r.close && r.alt) n.push(C.between + ' ' + r.alt + '.');
      if (r.highEnd) n.push(C.sizeUp);
      if (!r.fits) n.push(C.outside);
      return n.join(' ');
    }
    stockLine(r) {
      const C = this.copy, v = r.variant; if (!v) return null;
      if (v.state === 'ok') return h('span', { class: 'msh__ro-stock msh__ro-stock--ok' }, [h('strong', {}, [v.label]), ' ' + C.inStock]);
      if (v.state === 'alt') return h('span', { class: 'msh__ro-stock' }, [v.missing + ' ' + C.soldOut + '. ', h('strong', {}, [v.label]), ' ' + C.altInStock + '.']);
      if (v.state === 'out') return h('span', { class: 'msh__ro-stock' }, [v.label + ' ' + C.soldOut + '.']);
      return h('span', { class: 'msh__ro-stock' }, [C.notMade + ' ' + v.label + '.']);
    }
    links() {
      const C = this.copy, sg = this.getAttribute('size-guide'), ct = this.getAttribute('contact');
      if (!sg && !ct) return null;
      return h('div', { class: 'msh__links' }, [
        sg ? h('a', { class: 'msh__link', href: sg }, [C.sizeGuide]) : null,
        ct ? h('a', { class: 'msh__link', href: ct }, [C.contact]) : null
      ]);
    }
    render() {
      const self = this, C = this.copy; this.innerHTML = '';
      const box = h('div', { class: 'msh' }, []);
      const r = this.result;
      if (r && !this.open) {
        r.variant = this.stockFor(r);
        const note = this.notes(r);
        box.appendChild(h('div', { class: 'msh__set' }, [
          h('p', { class: 'msh__readout' }, [
            h('span', { class: 'msh__ro-label' }, [C.readout]), h('span', { class: 'msh__ro-size' }, [r.primary]),
            note ? h('span', { class: 'msh__ro-note' }, [note]) : null, this.stockLine(r)
          ]),
          h('p', { class: 'msh__set-actions' }, [
            h('button', { type: 'button', class: 'msh__link', onclick: function () { self.open = true; self.render(); } }, [C.change]), ' · ',
            h('button', { type: 'button', class: 'msh__link', onclick: function () { self.clear(); } }, [C.clear])
          ])
        ]));
        const l = this.links(); if (l) box.appendChild(l);
        this.appendChild(box); return;
      }
      const youth = this.gender === 'YOUTH', metric = this.units === 'metric', prev = this.draft || this.entry;
      const num = function (name, unit, value, min, max) {
        return h('span', { class: 'msh__wrap' }, [
          h('input', { type: 'number', inputmode: 'decimal', name: name, min: min, max: max, step: 1, value: value != null && value !== '' ? value : null, class: 'msh__input', 'aria-label': unit, oninput: function () { update(); } }),
          h('span', { class: 'msh__unit', 'aria-hidden': 'true' }, [unit])
        ]);
      };
      const inches = prev ? prev.h : null, lbs = prev ? prev.w : null;
      const heightFields = metric ? [num('cm', 'cm', inches ? Math.round(inches * 2.54) : '', 100, 220)]
        : [num('ft', 'ft', inches ? Math.floor(inches / 12) : '', 3, 7), num('in', 'in', inches ? Math.round(inches % 12) : '', 0, 11)];
      const weightFields = metric ? [num('kg', 'kg', lbs ? Math.round(lbs / 2.2046) : '', 20, 160)] : [num('lb', 'lb', lbs ? Math.round(lbs) : '', 40, 350)];
      const readout = h('p', { class: 'msh__readout', 'aria-live': 'polite' }, []);
      const submit = h('button', { type: 'submit', class: 'msh__btn', disabled: 'disabled' }, [C.submit]);
      let current = null;
      const form = h('form', { class: 'msh__form', novalidate: 'novalidate', onsubmit: function (e) { e.preventDefault(); if (current) { self.draft = null; self.set(current); } } }, [
        h('div', { class: 'msh__fields' }, [
          h('label', { class: 'msh__field' }, [h('span', { class: 'msh__label' }, [C.height]), h('span', { class: 'msh__inputs' }, heightFields)]),
          h('label', { class: 'msh__field' }, [h('span', { class: 'msh__label' }, [youth ? C.weightOptional : C.weight]), h('span', { class: 'msh__inputs' }, weightFields)]),
          h('div', { class: 'msh__units', role: 'group', 'aria-label': 'Units' }, [
            h('button', { type: 'button', class: 'msh__unitbtn' + (!metric ? ' is-on' : ''), 'aria-pressed': String(!metric), onclick: function () { self.draft = current || prev; self.units = 'imperial'; self.render(); } }, [C.imperial]),
            h('button', { type: 'button', class: 'msh__unitbtn' + (metric ? ' is-on' : ''), 'aria-pressed': String(metric), onclick: function () { self.draft = current || prev; self.units = 'metric'; self.render(); } }, [C.metric])
          ])
        ]),
        readout,
        h('div', { class: 'msh__actions' }, [
          submit,
          r ? h('button', { type: 'button', class: 'msh__link', onclick: function () { self.draft = null; self.open = false; self.render(); } }, [C.cancel]) : null
        ])
      ]);
      function val(n) { const el = form.querySelector('[name="' + n + '"]'); const v = el ? parseFloat(el.value) : NaN; return isNaN(v) ? null : v; }
      function update() {
        let hIn = null, wLb = null;
        if (self.units === 'metric') { const cm = val('cm'); hIn = cm ? cm / 2.54 : null; const kg = val('kg'); wLb = kg ? kg * 2.2046 : null; }
        else { const ft = val('ft'), inch = val('in'); hIn = ft ? ft * 12 + (inch || 0) : null; wLb = val('lb'); }
        const okH = hIn && hIn >= 40 && hIn <= 86, okW = youth ? true : (wLb && wLb >= 40 && wLb <= 350);
        current = (okH && okW) ? { h: Math.round(hIn * 10) / 10, w: wLb ? Math.round(wLb) : 0 } : null;
        readout.innerHTML = '';
        if (current) {
          const rr = sizeFor(self.gender, current.h, current.w);
          readout.appendChild(h('span', { class: 'msh__ro-label' }, [C.readout]));
          readout.appendChild(h('span', { class: 'msh__ro-size' }, [rr.primary]));
          const note = self.notes(rr); if (note) readout.appendChild(h('span', { class: 'msh__ro-note' }, [note]));
          submit.removeAttribute('disabled');
        } else submit.setAttribute('disabled', 'disabled');
      }
      if (C.title) box.appendChild(h('h3', { class: 'msh__title' }, [C.title]));
      if (C.lede) box.appendChild(h('p', { class: 'msh__lede' }, [C.lede]));
      box.appendChild(form);
      const l = this.links(); if (l) box.appendChild(l);
      this.appendChild(box);
      update();
    }
  }
  customElements.define('matuse-size-helper', SizeHelper);
  window.MatuseSizeHelper = { CHARTS: CHARTS, sizeFor: sizeFor, normSize: normSize, COPY: COPY };
})();
